import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "../endpoint.js";
import { NEEDS_REVIEW_REASONS } from "./commands.js";
/** POS commands that need a person (docs/specs/pos-sync.md "needs_review reasons", B-43). */
export const SYNC_REVIEW_STATUSES = ["open", "acknowledged", "reprocessed", "voided"];
export const syncReviewStatusSchema = z.enum(SYNC_REVIEW_STATUSES);
export const SYNC_REVIEW_ACTIONS = ["acknowledge", "reprocess", "void"];
export const syncReviewSchema = z.object({
    id: z.uuid(),
    mutationId: z.uuid(),
    deviceId: z.uuid(),
    deviceSeq: z.int(),
    locationId: z.uuid(),
    type: z.string(),
    reasons: z.array(z.enum(NEEDS_REVIEW_REASONS)),
    /** False: the command is held and changed nothing yet (reprocess or void it). */
    applied: z.boolean(),
    /** Error code when processing failed. */
    detail: z.string(),
    /** Ids and numbers the command produced (`orderNumber`, …). */
    refs: z.record(z.string(), z.string()),
    status: syncReviewStatusSchema,
    note: z.string(),
    occurredAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
    resolvedAt: z.iso.datetime().nullable(),
    resolvedBy: z.uuid().nullable(),
    version: z.int(),
});
export const syncReviewDetailSchema = syncReviewSchema.extend({
    /** The command as the device sent it (kept for held commands). */
    payload: z.unknown().nullable(),
});
export const syncReviewListQuerySchema = z.object({
    status: syncReviewStatusSchema.optional(),
    locationId: z.uuid().optional(),
    cursor: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const syncReviewListSchema = z.object({
    data: z.array(syncReviewSchema),
    nextCursor: z.uuid().nullable(),
});
export const resolveSyncReviewRequestSchema = z
    .object({
    action: z.enum(SYNC_REVIEW_ACTIONS),
    note: z.string().trim().max(500).default(""),
    version: z.int().min(1),
})
    .strict()
    .refine((r) => r.action !== "void" || r.note.length > 0, { message: "Say why", path: ["note"] });
export const waiveGapRequestSchema = z
    .object({ throughSeq: z.int().min(1), note: z.string().trim().min(1).max(500) })
    .strict();
export const deviceSyncStateSchema = z.object({
    deviceId: z.uuid(),
    lastSeq: z.int().min(0),
    gapExpectedSeq: z.int().nullable(),
});
const tag = { tag: "pos-sync" };
export const listSyncReviews = defineEndpoint({
    ...tag,
    name: "listSyncReviews",
    method: "GET",
    path: "/v1/sync-reviews",
    access: { permission: "sync_reviews.view" },
    summary: "POS commands that need review, newest first; location-scoped",
    query: syncReviewListQuerySchema,
    status: 200,
    response: syncReviewListSchema,
});
export const getSyncReview = defineEndpoint({
    ...tag,
    name: "getSyncReview",
    method: "GET",
    path: "/v1/sync-reviews/{id}",
    access: { permission: "sync_reviews.view" },
    summary: "One review with the command as sent",
    params: idParamsSchema,
    status: 200,
    response: syncReviewDetailSchema,
});
export const resolveSyncReview = defineEndpoint({
    ...tag,
    name: "resolveSyncReview",
    method: "POST",
    path: "/v1/sync-reviews/{id}/resolve",
    access: { permission: "sync_reviews.resolve" },
    summary: "Acknowledge an applied command, or reprocess / void a held one (void needs a note)",
    params: idParamsSchema,
    body: resolveSyncReviewRequestSchema,
    status: 200,
    response: syncReviewDetailSchema,
    idempotent: true,
    errors: ["CONFLICT", "INVALID_TRANSITION"],
});
export const waiveDeviceGap = defineEndpoint({
    ...tag,
    name: "waiveDeviceGap",
    method: "POST",
    path: "/v1/devices/{id}/waive-gap",
    access: { permission: "sync_reviews.resolve" },
    summary: "Skip a device's missing commands up to `throughSeq` when they cannot be recovered (audited)",
    params: idParamsSchema,
    body: waiveGapRequestSchema,
    status: 200,
    response: deviceSyncStateSchema,
    errors: ["BUSINESS_RULE_VIOLATION"],
});
//# sourceMappingURL=reviews.js.map